import React from 'react';
import { getRem, transitionAnimation, typographyBody1, typographyBody2 } from 'lib/core';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask-3.0';
import styled, { css } from 'styled-components';

import { DATE_INPUT_MASKS } from './constants';
import { BORDER_POSITIONS, DIVIDER_POSITIONS, INPUT_SIZES } from '../../../input/constants';
import { DATE_FORMATS } from '../../constants';

const StyledInputMask = styled(InputMask)`
  border: none;
  ${transitionAnimation('border, box-shadow')}
  ${({ theme }) => typographyBody1(theme)}
  background: transparent;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  min-width: 0;
  outline: none;
  padding: 0;
  padding-right: ${({ theme }) => theme.size.spacing.small.value};
  width: 100%;

  ${({ $size, theme }) =>
    $size === INPUT_SIZES.LARGE
      ? css`
          ${typographyBody2(theme)};
          height: ${getRem(24)};
        `
      : css`
          height: ${getRem(20)};
        `}

  &::placeholder {
    color: ${({ theme }) => theme.color.gray[600].value};
    font-weight: ${({ theme }) => theme.font.weight.light.value};
  }

  &::-ms-clear,
  &::-ms-reveal {
    display: none;
  }
  ${({ $isObscured, theme }) =>
    $isObscured &&
    css`
      font-family: ${theme.font.family.obscured.value};
    `}
`;

const ReactDateInputMask = ({
  dateFormat,
  flexibleDateInput = false,
  hideBorder = BORDER_POSITIONS.NONE,
  inputDivider = DIVIDER_POSITIONS.NONE,
  inputRef = undefined,
  maxLength = undefined,
  size = INPUT_SIZES.STANDARD,
  ...other
}) => {
  // By default, on focus new value becomes dateFormat. For this case we want to set it back to an empty string
  const beforeMaskedStateChange = ({ nextState }) => {
    const value = nextState.value === dateFormat ? '' : nextState.value;

    return {
      ...nextState,
      value,
    };
  };

  const dateMask = flexibleDateInput ? undefined : DATE_INPUT_MASKS[dateFormat];

  return (
    <StyledInputMask
      beforeMaskedStateChange={beforeMaskedStateChange}
      $hideBorder={hideBorder}
      $inputDivider={inputDivider}
      maskPlaceholder={dateFormat}
      mask={dateMask}
      {...other}
      placeholder={dateFormat}
      ref={inputRef}
      $size={size}
    />
  );
};

ReactDateInputMask.propTypes = {
  /** Defines in what format date input should format value (e.g. YYYY/MM/DD, MM/DD/YYYY, DD/MM/YYYY) */
  dateFormat: PropTypes.oneOf(Object.values(DATE_FORMATS)).isRequired,
  /** overrides the mask if true */
  flexibleDateInput: PropTypes.bool,
  /** Defines which side of input container border should be hidden */
  hideBorder: PropTypes.oneOf(Object.values(BORDER_POSITIONS)),
  /** Defines the side of vertical divider */
  inputDivider: PropTypes.oneOf(Object.values(DIVIDER_POSITIONS)),
  /** Ref that should be passed to input component */
  inputRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  /** This prop is not used by component. We destructure it from props because
   * it should not be supplied to InputMask component */
  maxLength: PropTypes.number,
  /** Set the size of the input */
  size: PropTypes.oneOf(Object.values(INPUT_SIZES)),
};

export { ReactDateInputMask };
